'use client';

import { type ReactNode, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { AppRouterCacheProvider } from '@mui/material-nextjs/v15-appRouter';
import CssBaseline from '@mui/material/CssBaseline';
import { muiTheme } from '@microservices/design-system';
import { AuthProvider, PermissionProvider } from '@/infrastructure/auth';
import { NovuClientProvider, NotificationRealtimeProvider } from '@/infrastructure/notifications';
import { ToastProvider } from '@/infrastructure/toast';

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            refetchOnWindowFocus: false,
            /** Avoid long backoff chains when BFF returns 401 after session revocation. */
            retry: false,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PermissionProvider>
          <AppRouterCacheProvider options={{ enableCssLayer: true }}>
            <MuiThemeProvider theme={muiTheme}>
              <CssBaseline />
              <ToastProvider>
                <NovuClientProvider>
                  <NotificationRealtimeProvider>
                    {children}
                  </NotificationRealtimeProvider>
                </NovuClientProvider>
              </ToastProvider>
            </MuiThemeProvider>
          </AppRouterCacheProvider>
        </PermissionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
